import React from "react";
import {Badge, Button, Col, Form, Row, Spinner, Table} from "react-bootstrap";
import {CheckCircleFill, XCircleFill} from "react-bootstrap-icons";
import '../CommonCss/TableSmall.css'
import {SynergyFinderPipelineStatus} from "./Const";
import {BatchPipelineRequest} from "./SynergyFinderInputForm";
import {
    ColumnDef,
    SortingState,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    useReactTable,
} from "@tanstack/react-table";

export interface Pipeline extends BatchPipelineRequest {
    batchPipelineRequestUuid: string,
    drug1: string,
    drug2: string,
    geneStatus: string
    cellLine: string,
    s3KeyOutputGraphImage: string,
    s3KeyOutputGraphImageSmall: string,
    s3KeyOutputExcel: string,
    deleted: boolean,
}

export const TanStackSynergyFinderHistoryTable: React.FC<{
    data: Pipeline[];
    searchTerm: string;
    onSearchChange: (term: string) => void;
    onRowSelect: (rows: Pipeline[], isSelected: boolean) => void;
}> = ({data, searchTerm, onSearchChange, onRowSelect}) => {
    const [rowSelection, setRowSelection] = React.useState<Record<string, boolean>>({})
    const [sorting, setSorting] = React.useState<SortingState>([
        { id: 'uploadTimestamp', desc: true }
    ])
    const [pageSize, setPageSize] = React.useState(10)
    const [pageIndex, setPageIndex] = React.useState(0)

    const columns = React.useMemo<ColumnDef<Pipeline, any>[]>(() => [
        {
            id: 'select',
            // DO NOT allow to select all rows because it trigger hundreds of download requests to S3 and AWS may block the requests
            // header: ({ table }) => (
            //     <input
            //         type="checkbox"
            //         checked={
            //             table.getPreFilteredRowModel().rows.length > 0 &&
            //             table.getPreFilteredRowModel().rows.every(row => row.getIsSelected())
            //         }
            //         onChange={(e) => {
            //             table.toggleAllRowsSelected(e.target.checked)
            //             const allRows = table.getPreFilteredRowModel().rows.map(row => row.original)
            //             onRowSelect(allRows, e.target.checked)
            //         }}
            //     />
            // ),
            cell: ({ row }) => (
                <input
                    type="checkbox"
                    checked={row.getIsSelected()}
                    onChange={(e) => {
                        row.toggleSelected(e.target.checked)
                        onRowSelect([row.original], e.target.checked)
                    }}
                    onClick={(e) => e.stopPropagation()}
                />
            ),
            enableSorting: false,
            enableColumnFilter: false,
        },
        {
            accessorKey: 'description',
            header: 'Description',
            cell: info => (
                <span style={{
                    display: 'block',
                    width: 450,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                }}>
          {info.getValue() as string}
        </span>
            ),
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            accessorKey: 'calculationStatus',
            header: 'Progress',
            cell: info => {
                const value = info.getValue() as string
                switch (value) {
                    case SynergyFinderPipelineStatus.IN_PROGRESS:
                        return <Spinner animation="border" variant="primary" size="sm" />
                    case SynergyFinderPipelineStatus.COMPLETED:
                        return <CheckCircleFill color="rgba(var(--bs-success-rgb),var(--bs-bg-opacity))" />
                    case SynergyFinderPipelineStatus.FAILED:
                        return <XCircleFill color="rgba(var(--bs-danger-rgb),var(--bs-bg-opacity))" />
                    default:
                        return <Badge bg="secondary">{value}</Badge>
                }
            },
            enableSorting: false,
            enableColumnFilter: false,
        },
        {
            accessorKey: 'geneStatus',
            header: 'Gene Status',
            cell: info => {
                const value = info.getValue() as string
                switch (value) {
                    case SynergyFinderPipelineStatus.IN_PROGRESS:
                        return <Spinner animation="border" variant="primary" size="sm" />
                    default:
                        return value
                }
            },
            enableSorting: true,
            enableColumnFilter: true,
        },
        {
            accessorKey: 'drug1',
            header: 'Drug 1',
            cell: info => {
                const value = info.getValue() as string
                switch (value) {
                    case SynergyFinderPipelineStatus.IN_PROGRESS:
                        return <Spinner animation="border" variant="primary" size="sm" />
                    default:
                        return value
                }
            },
            enableSorting: true,
            enableColumnFilter: true,
        },
        {
            accessorKey: 'drug2',
            header: 'Drug 2',
            enableSorting: true,
            enableColumnFilter: true,
        },
        {
            accessorKey: 'cellLine',
            header: 'Cell line',
            enableSorting: true,
            enableColumnFilter: true,
        },
        {
            accessorKey: 'normalization',
            header: 'Normalization',
            cell: info => {
                const value = info.getValue() as boolean
                switch (value) {
                    case false:
                        return "No"
                    case true:
                        return "Yes"
                    default:
                        return "Unknown"
                }
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            accessorKey: 'calculationMethod',
            header: 'Calculation Method',
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            accessorKey: 'uuid',
            header: 'Job ID',
            meta: { hidden: true },
            enableSorting: false,
            enableColumnFilter: false,
        },
        {
            accessorKey: 'uploadTimestamp',
            header: 'Date',
            cell: info => {
                const value = info.getValue() as string;
                if (!value) {
                    return ""
                }
                return new Date(value).toISOString().split('T')[0]
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            accessorKey: 'owner',
            header: 'Owner',
            enableSorting: true,
            enableColumnFilter: true,
        },
    ], [onRowSelect])

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
            rowSelection,
            globalFilter: searchTerm,
            pagination: {
                pageIndex,
                pageSize,
            }
        },
        onSortingChange: setSorting,
        onRowSelectionChange: setRowSelection,
        onGlobalFilterChange: onSearchChange,
        getSortedRowModel: getSortedRowModel(),
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        enableRowSelection: true,
        enableMultiRowSelection: true,
        enableSorting: true,
        manualPagination: false,
        onPaginationChange: (updater) => {
            const next = typeof updater === "function" ? updater({pageIndex, pageSize}) : updater
            setPageIndex(next.pageIndex)
            setPageSize(next.pageSize)
        }
    })

    const paginationSize = [10, 20, 40]

    return (
        <>
            <Row className="mt-2">
                <div className="table-responsive">
                    <Table bordered hover>
                        <thead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    // Skip hidden columns
                                    if (header.column.columnDef.meta?.hidden) return null

                                    return (
                                        <th key={header.id}
                                            style={{cursor: header.column.getCanSort() ? 'pointer' : 'default'}}
                                        >
                                            <div className='d-flex justify-content-between align-items-center w-100'>
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                {header.column.getCanSort() && (
                                                    <span className="align-self-end mt-1"
                                                          onClick={header.column.getToggleSortingHandler()}
                                                          style={{
                                                              display: 'inline-block',
                                                              marginLeft: '4px',
                                                              cursor: 'pointer'
                                                          }}
                                                    >
                                                        {{
                                                            asc: <span style={{color: 'darkgreen'}}>▲</span>,
                                                            desc: <span style={{color: 'darkgreen'}}>▼</span>
                                                        }[header.column.getIsSorted() as string] || '▼'}
                                                    </span>
                                                )}
                                            </div>
                                            {/* Render filterComponent if defined */}
                                            {header.column.getCanFilter() && (
                                                <div>
                                                    <input
                                                        type="text"
                                                        value={(header.column.getFilterValue() ?? '') as string}
                                                        onChange={e => header.column.setFilterValue(e.target.value)}
                                                        placeholder="Search"
                                                        className="form-control form-control-sm"
                                                    />
                                                </div>
                                            )}
                                        </th>
                                    )
                                })}
                            </tr>
                        ))}
                        </thead>
                        <tbody>
                        {table.getRowModel().rows.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length - 1}> {/* -1 for hidden column */}
                                    <div className="text-center p-3">No data found</div>
                                </td>
                            </tr>
                        ) : (
                            table.getRowModel().rows.map(row => (
                                <tr key={row.id}>
                                    {row.getVisibleCells().map(cell =>
                                        // Skip hidden columns
                                        !cell.column.columnDef.meta?.hidden ? (
                                            <td key={cell.id}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        ) : null
                                    )}
                                </tr>
                            ))
                        )}
                        </tbody>
                    </Table>
                </div>
            </Row>

            {/* Pagination */}
            <Row className="mt-3">
                <Col xs={12} className="d-flex justify-content-between align-items-center">
                    <div className={"d-flex align-items-center gap-2"}>
                        <Form.Select
                            size={"sm"}
                            style={{width: "auto"}}
                            value={pageSize}
                            onChange={(e) => setPageSize(Number(e.target.value))}
                        >
                            {paginationSize.map((size) => (
                                <option key={size} value={size}>{size}</option>
                            ))}
                        </Form.Select>
                        <span>
                        {`Showing rows ${
                            table.getFilteredRowModel().rows.length === 0
                                ? 0
                                : pageIndex * pageSize + 1
                        } to ${
                            Math.min((pageIndex + 1) * pageSize, table.getFilteredRowModel().rows.length)
                        } of ${table.getFilteredRowModel().rows.length}`}
                    </span>
                    </div>

                    <div className={"d-flex align-items-center gap-2"}>
                        <Button
                            onClick={() => setPageIndex(0)}
                            disabled={table.getState().pagination.pageIndex === 0}
                        >«</Button>
                        <Button
                            onClick={() => table.previousPage()}
                            disabled={!table.getCanPreviousPage()}
                        >‹</Button>
                        <span>Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}</span>
                        <Button
                            onClick={() => table.nextPage()}
                            disabled={!table.getCanNextPage()}
                        >›</Button>
                        <Button
                            onClick={() => setPageIndex(table.getPageCount() - 1)}
                            disabled={!table.getCanNextPage()}
                        >»</Button>
                    </div>
                </Col>
            </Row>
        </>
    )
}